import { t } from "@lingui/macro";
import { OptimizedDataDto } from "@reactive-resume/dto";
import { ChartLineUp, Warning } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useRef } from "react";
import { useToast } from "@/client/hooks/use-toast";
import { useResumeOptimization } from "@/client/hooks/use-resume-optimization";

import { CompareMetrics } from "@/client/pages/builder/_components/ai-optimize/components/CompareMetrics";
import { RejectDialog } from "@/client/pages/builder/_components/ai-optimize/dialogs/RejectDialog";
import { ApplyDialog } from "@/client/pages/builder/_components/ai-optimize/dialogs/ApplyDialog";
import { PageLoading } from "@/client/pages/builder/_components/ai-optimize/components/Loadings";
import { CompareIframes } from "@/client/pages/builder/_components/ai-optimize/components/CompareIframes";
import useDiff, { DiffResumeData } from "./components/useDiff";
import { emptyResume } from "@/artboard/store/artboard";

interface CompareProps {
  optimizationData: OptimizedDataDto;
  onAccept: () => void;
  onReject: () => void;
}

export const Compare = ({ optimizationData, onAccept, onReject }: CompareProps) => {
  const { toast } = useToast();
  const { deleteOptimizationData, acceptOptimizationData, isDeleting, isAccepting } =
    useResumeOptimization();

  const originalFrameRef = useRef<HTMLIFrameElement>(null);
  const optimizedFrameRef = useRef<HTMLIFrameElement>(null);
  const [originalFrameLoaded, setOriginalFrameLoaded] = useState(false);
  const [optimizedFrameLoaded, setOptimizedFrameLoaded] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const sectionHeights = useRef({
    original: null,
    optimized: null,
  });

  // Access data directly without type casting
  const initialAnalysis = optimizationData?.initialAnalysis;
  const analysis = optimizationData?.analysis;
  const initialResume = {
    ...optimizationData?.initialResume,
    sections: {
      ...emptyResume.sections,
      ...optimizationData?.initialResume?.sections,
    },
  };
  const optimizedResume = {
    ...optimizationData?.resume,
    sections: {
      ...emptyResume.sections,
      ...optimizationData?.resume?.sections,
    },
  };

  const diffData = useDiff(
    initialResume as unknown as DiffResumeData,
    optimizedResume as unknown as DiffResumeData,
  );

  useEffect(() => {
    const handlePageLoadMessage = (event: MessageEvent) => {
      if (
        event.origin !== window.location.origin ||
        !optimizedFrameRef.current ||
        !optimizedFrameRef.current.contentWindow ||
        !originalFrameRef.current ||
        !originalFrameRef.current.contentWindow
      )
        return;

      if (event.data.type === "PAGE_LOADED") {
        const { frameId } = event.data.payload;
        if (frameId !== "original" && frameId !== "optimized") {
          console.error("Invalid frameId", frameId);
        }

        if (frameId === "optimized") {
          optimizedFrameRef.current.contentWindow.postMessage(
            { type: "SET_RESUME", payload: optimizedResume },
            "*",
          );
          setOptimizedFrameLoaded(true);
        } else {
          originalFrameRef.current.contentWindow.postMessage(
            { type: "SET_RESUME", payload: initialResume },
            "*",
          );
          setOriginalFrameLoaded(true);
        }
      } else if (event.data.type === "SECTION_HEIGHTS_CALCULATED") {
        const { frameId } = event.data.payload;
        if (frameId !== "original" && frameId !== "optimized") {
          console.error("Invalid frameId", frameId);
        }

        // section heights change
        sectionHeights.current = {
          ...sectionHeights.current,
          [frameId]: event.data.payload.sections,
        };

        if (sectionHeights.current.original && sectionHeights.current.optimized) {
          const mergedMax = (
            original: Record<string, number> | null,
            optimized: Record<string, number> | null,
          ) => {
            const result: Record<string, number> = {};

            const keys = new Set([...Object.keys(original || {}), ...Object.keys(optimized || {})]);

            keys.forEach((key) => {
              const orig = original?.[key] ?? 0;
              const opt = optimized?.[key] ?? 0;
              result[key] = Math.max(orig, opt);
            });

            return result;
          };

          const changeSectionHeightsMessage = {
            type: "SECTION_HEIGHTS",
            payload: {
              sections: mergedMax(
                sectionHeights.current.original,
                sectionHeights.current.optimized,
              ),
            },
          };
          originalFrameRef.current.contentWindow.postMessage(changeSectionHeightsMessage, "*");
          optimizedFrameRef.current.contentWindow.postMessage(changeSectionHeightsMessage, "*");
        }
      }
    };

    window.addEventListener("message", handlePageLoadMessage);

    return () => {
      window.removeEventListener("message", handlePageLoadMessage);
    };
  }, []);

  // After both iframes are loaded, send diff data
  useEffect(() => {
    if (!originalFrameLoaded || !optimizedFrameLoaded || !initialResume || !optimizedResume) return;
    if (!diffData) return;

    console.log("Sending diff data to iframes", diffData);
    if (originalFrameRef.current?.contentWindow) {
      originalFrameRef.current.contentWindow.postMessage(
        { type: "SET_DIFF_DATA", payload: diffData },
        "*",
      );
    }
    if (optimizedFrameRef.current?.contentWindow) {
      optimizedFrameRef.current.contentWindow.postMessage(
        { type: "SET_DIFF_DATA", payload: diffData },
        "*",
      );
    }
  }, [originalFrameLoaded, optimizedFrameLoaded, initialResume, optimizedResume, diffData]);

  const handleReject = async () => {
    // Prevent multiple calls
    if (isDeleting || isAccepting) return;

    setShowRejectDialog(false);

    try {
      const success = await deleteOptimizationData();
      if (success) {
        // Show success toast
        toast({
          title: t`Optimization Rejected`,
          description: t`The optimized resume has been rejected and removed.`,
          variant: "success",
        });
      } else {
        toast({
          title: t`Error`,
          description: t`Failed to reject optimization, please try again later.`,
          variant: "error",
        });
      }
    } catch (error) {
      toast({
        title: t`Error`,
        description: t`Failed to reject optimization, please try again later.`,
        variant: "error",
      });
      console.error("Error rejecting optimization:", error);
    } finally {
      // Always close the component regardless of success/failure
      onReject();
    }
  };
  const handleApply = async () => {
    // Prevent multiple calls
    if (isAccepting || isDeleting) return;

    setShowApplyDialog(false);

    try {
      const success = await acceptOptimizationData();
      if (success) {
        // Show success toast
        toast({
          title: t`Optimization Applied`,
          description: t`Your resume has been successfully updated with the optimized content.`,
          variant: "success",
        });
      } else {
        toast({
          title: t`Error`,
          description: t`Failed to apply optimization, please try again later.`,
          variant: "error",
        });
      }
    } catch (error) {
      toast({
        title: t`Error`,
        description: t`Failed to apply optimization, please try again later.`,
        variant: "error",
      });
      console.error("Error applying optimization:", error);
    } finally {
      // Always close the component regardless of success/failure
      onAccept();
    }
  };

  const hasAnalysisData = Boolean(
    initialAnalysis && analysis && initialAnalysis.metrics && analysis.metrics,
  );
  const isLoaded = Boolean(initialResume && optimizedResume);

  // Function to detect poor optimization performance
  const detectPoorOptimization = () => {
    if (!hasAnalysisData) return false;

    const initialOverallScore = initialAnalysis.overallWeightedScore || 0;
    const optimizedOverallScore = analysis.overallWeightedScore || 0;

    // Check if overall score dropped
    if (optimizedOverallScore < initialOverallScore) {
      return true;
    }

    // Check if any individual metric dropped significantly (>15%)
    const SIGNIFICANT_DROP_THRESHOLD = 0.15;

    for (const [metricKey, initialMetric] of Object.entries(initialAnalysis.metrics)) {
      const optimizedMetric = analysis.metrics[metricKey];
      if (!optimizedMetric || !initialMetric) continue;

      // Type assertion to tell TypeScript that these objects have the expected properties
      const typedInitialMetric = initialMetric as { weight: number; weightedScore: number };
      const typedOptimizedMetric = optimizedMetric as { weight: number; weightedScore: number };

      const initialScore =
        typedInitialMetric.weight > 0
          ? typedInitialMetric.weightedScore / typedInitialMetric.weight
          : 0;
      const optimizedScore =
        typedOptimizedMetric.weight > 0
          ? typedOptimizedMetric.weightedScore / typedOptimizedMetric.weight
          : 0;

      const dropPercentage = (initialScore - optimizedScore) / initialScore;

      if (dropPercentage > SIGNIFICANT_DROP_THRESHOLD) {
        return true;
      }
    }

    return false;
  };

  const showOptimizationWarning = hasAnalysisData && detectPoorOptimization();

  // Warning component
  const OptimizationWarning = () => (
    <motion.div
      className="mx-4 mb-4 rounded-lg border-2 border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      <div className="flex items-start gap-3">
        <Warning size={20} className="mt-0.5 text-amber-600 dark:text-amber-400" weight="fill" />
        <div className="flex-1">
          <h3 className="mb-1 text-sm font-semibold text-amber-800 dark:text-amber-200">
            {t`Optimization Results May Not Be Optimal`}
          </h3>
          <p className="text-sm leading-relaxed text-amber-700 dark:text-amber-300">
            {t`The optimization process resulted in lower scores or significant drops in some metrics. Please review the changes thoroughly before applying them. Consider rejecting these changes and trying optimization again with different answers or focus areas.`}
          </p>
        </div>
      </div>
    </motion.div>
  );

  return (
    <>
      <RejectDialog
        open={showRejectDialog}
        onOpenChange={setShowRejectDialog}
        onReject={handleReject}
        isDeleting={isDeleting}
      />
      <ApplyDialog
        open={showApplyDialog}
        onOpenChange={setShowApplyDialog}
        onApply={handleApply}
        isAccepting={isAccepting}
      />

      <AnimatePresence mode="wait">
        <motion.div
          className="fixed inset-0 z-50 w-screen overflow-y-auto bg-gray-50 dark:bg-gray-900"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        >
          <div className="flex flex-col">
            {/* Header */}
            <motion.div
              className="border-foreground/10 flex-shrink-0 border-b px-4 py-3"
              initial={{ y: -30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
            >
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 text-xl font-semibold">
                  <ChartLineUp size={20} className="text-primary" weight="fill" />
                  {t`Metrics Summary`}
                </h2>
                <p className="text-foreground/50 text-sm font-medium">
                  {t`Review your optimized resume and choose to Apply or Reject the changes`}
                </p>
              </div>
            </motion.div>

            {/* Optimization Warning */}
            {showOptimizationWarning && <OptimizationWarning />}

            <div className="grid-cols-12 2xl:grid [&>div:nth-child(1)]:order-2 [&>div:nth-child(1)]:col-span-3 [&>div:nth-child(2)]:order-1 [&>div:nth-child(2)]:col-span-9">
              {/* Metrics panel  */}
              {hasAnalysisData && <CompareMetrics optimizationData={optimizationData} />}

              <motion.div
                className="flex flex-1"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, ease: "easeOut", delay: 0.3 }}
              >
                <div className={`flex-1 p-4`}>
                  {!isLoaded && <PageLoading />}

                  {isLoaded && initialResume && optimizedResume && (
                    <CompareIframes
                      originalFrameRef={originalFrameRef}
                      optimizedFrameRef={optimizedFrameRef}
                      originalFrameLoaded={originalFrameLoaded}
                      optimizedFrameLoaded={optimizedFrameLoaded}
                      isDeleting={isDeleting}
                      isAccepting={isAccepting}
                      setShowRejectDialog={setShowRejectDialog}
                      setShowApplyDialog={setShowApplyDialog}
                      diffData={diffData}
                    />
                  )}
                </div>
              </motion.div>
            </div>
            <p className="text-foreground/50 mb-4 text-center text-sm font-medium">
              {t`Review your optimized resume and choose to Apply or Reject the changes`}
            </p>
          </div>
        </motion.div>
      </AnimatePresence>
    </>
  );
};
